import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence, Reorder, useDragControls } from 'framer-motion'
import {
  X,
  CheckCircle,
  Circle,
  Medal,
  CurrencyDollar,
  CurrencyEur,
  List,
  Coins,
} from '@phosphor-icons/react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { AVAILABLE_WIDGETS } from '@/utils/abacus'

interface WidgetSelectorProps {
  isOpen: boolean
  onClose: () => void
  selectedWidgets: string[]
  onToggleWidget: (widgetId: string) => void
  onReorderWidgets?: (newOrder: string[]) => void
}

export function WidgetSelector({
  isOpen,
  onClose,
  selectedWidgets,
  onToggleWidget,
  onReorderWidgets,
}: WidgetSelectorProps) {
  const [localWidgets, setLocalWidgets] = useState<string[]>([])

  // Senkronize et: Açıldığında mevcut sırayı al
  useEffect(() => {
    if (isOpen) {
      setLocalWidgets(selectedWidgets)
    }
  }, [isOpen, selectedWidgets])

  // Helper to get icon for widget
  const getIcon = (id: string) => {
    const isSilver = id.includes('silver')
    const isGold = !isSilver && (id.includes('gold') || id.includes('gram'))
    const iconClass = cn(
      'w-5 h-5',
      isSilver ? 'text-[#8E9196]' : isGold ? 'text-[#DCB645]' : 'text-espresso'
    )

    switch (id) {
      case 'usd_try':
        return <CurrencyDollar className={iconClass} weight="bold" />
      case 'eur_try':
        return <CurrencyEur className={iconClass} weight="bold" />
      case 'gram_silver_try':
        return <Coins className={iconClass} weight="bold" />
      case 'gram_24_try':
      case 'gram_22_try':
        return <Coins className={iconClass} weight="bold" />
      case 'quarter_gold_try':
      case 'half_gold_try':
      case 'full_gold_try':
        return <Medal className={iconClass} weight="bold" />
      default:
        return <Coins className={iconClass} weight="bold" />
    }
  }

  const handleReorder = (newOrder: string[]) => {
    setLocalWidgets(newOrder)
    onReorderWidgets?.(newOrder)
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-espresso/40 backdrop-blur-md z-[60]"
          />

          {/* Premium Sheet */}
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 28, stiffness: 250 }}
            className="fixed inset-x-0 bottom-0 max-h-[92vh] bg-[#FCFAF8] rounded-t-[40px] shadow-[0_-20px_60px_rgba(0,0,0,0.15)] z-[70] flex flex-col overflow-hidden border-t border-espresso/5"
          >
            {/* Drag Indicator */}
            <div className="w-12 h-1.5 bg-espresso/10 rounded-full mx-auto mt-4 mb-2 shrink-0" />

            {/* Header */}
            <div className="px-8 pb-4 pt-2 flex justify-between items-center bg-transparent">
              <div className="flex flex-col">
                <span className="text-[11px] font-extrabold text-[#CFA02E] uppercase tracking-[0.2em] mb-1">
                  Özelleştir
                </span>
                <h2 className="text-[26px] font-sans font-extrabold text-espresso tracking-tight leading-tight">
                  Piyasa Takibi
                </h2>
              </div>
              <button
                onClick={onClose}
                className="w-11 h-11 flex items-center justify-center rounded-full bg-espresso/5 hover:bg-espresso/10 transition-all active:scale-90 text-espresso/60"
              >
                <X size={22} weight="bold" />
              </button>
            </div>

            {/* Subtitle */}
            <div className="px-8 pb-2">
              <p className="text-[14px] font-medium text-espresso/50 leading-relaxed">
                Görünmesini istediğiniz kurları seçin ve{' '}
                <span className="text-espresso font-bold">üzerlerine basılı tutarak</span>{' '}
                sıralamayı değiştirin.
              </p>
            </div>

            {/* Body - Reorderable List */}
            <div className="flex-1 overflow-y-auto px-6 py-4 custom-scrollbar pb-12">
              <Reorder.Group
                axis="y"
                values={localWidgets}
                onReorder={handleReorder}
                className="space-y-3"
              >
                {localWidgets.map(widgetId => (
                  <ReorderItemComponent
                    key={widgetId}
                    widgetId={widgetId}
                    onToggleWidget={onToggleWidget}
                  />
                ))}

                {/* Non-selected widgets */}
                {AVAILABLE_WIDGETS.filter(w => !localWidgets.includes(w.id)).map(widget => (
                  <div
                    key={widget.id}
                    onClick={() => onToggleWidget(widget.id)}
                    className="flex items-center justify-between p-4 px-5 rounded-[24px] border border-dashed border-espresso/10 bg-black/5 opacity-60 hover:opacity-80 transition-all cursor-pointer"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-[42px] h-[42px] rounded-full bg-espresso/5 flex items-center justify-center shrink-0">
                        <div className="grayscale opacity-50">{getIcon(widget.id)}</div>
                      </div>
                      <div className="flex flex-col">
                        <span className="font-bold text-[16px] text-espresso/40 tracking-tight">
                          {widget.label}
                        </span>
                      </div>
                    </div>
                    <div className="text-[28px] text-espresso/10">
                      <Circle weight="bold" />
                    </div>
                  </div>
                ))}
              </Reorder.Group>

              <p className="text-center text-[12px] font-semibold text-espresso/30 mt-6 mb-1 px-4 uppercase tracking-[0.1em]">
                Ana sayfada sıralama buna göre güncellenir.
              </p>
              <p className="text-center text-[10px] text-espresso/25 font-medium px-4 mb-2">
                Veriler: TCMB EVDS • Yahoo Finance
              </p>
            </div>

            {/* Footer */}
            <div className="p-8 pb-10 border-t border-espresso/5 bg-white/70 backdrop-blur-xl shrink-0">
              <Button
                onClick={onClose}
                variant="primary"
                fullWidth
                className="h-[64px] rounded-[22px] text-[17px] font-extrabold tracking-tight bg-gradient-to-br from-[#E6C96C] via-[#DCB645] to-[#B8922C] hover:opacity-90 text-espresso shadow-[0_12px_24px_rgba(212,175,55,0.25)] transition-all active:scale-[0.98] border border-white/20"
              >
                Seçimleri Kaydet
              </Button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

// --- Reorder Item Component with Drag Controls ---
interface ReorderItemProps {
  widgetId: string
  onToggleWidget: (id: string) => void
}

function ReorderItemComponent({ widgetId, onToggleWidget }: ReorderItemProps) {
  const controls = useDragControls()
  const widget = AVAILABLE_WIDGETS.find(w => w.id === widgetId)
  if (!widget) return null

  // Helper to get icon for widget
  const getIcon = (id: string) => {
    const isSilver = id.includes('silver')
    const isGold = !isSilver && (id.includes('gold') || id.includes('gram'))
    const iconClass = cn(
      'w-5 h-5',
      isSilver ? 'text-[#8E9196]' : isGold ? 'text-[#DCB645]' : 'text-espresso'
    )

    switch (id) {
      case 'usd_try':
        return <CurrencyDollar className={iconClass} weight="bold" />
      case 'eur_try':
        return <CurrencyEur className={iconClass} weight="bold" />
      case 'gram_silver_try':
        return <Coins className={iconClass} weight="bold" />
      case 'gram_24_try':
      case 'gram_22_try':
        return <Coins className={iconClass} weight="bold" />
      case 'quarter_gold_try':
      case 'half_gold_try':
      case 'full_gold_try':
        return <Medal className={iconClass} weight="bold" />
      default:
        return <Coins className={iconClass} weight="bold" />
    }
  }

  return (
    <Reorder.Item
      value={widgetId}
      dragListener={false}
      dragControls={controls}
      whileDrag={{
        scale: 1.05,
        boxShadow: '0 20px 40px rgba(0,0,0,0.12)',
        cursor: 'grabbing',
      }}
      className="group relative"
    >
      <div
        className={cn(
          'flex items-center justify-between p-4 px-5 rounded-[24px] border transition-all',
          'bg-white border-espresso/5 shadow-[0_4px_12px_rgba(0,0,0,0.03)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.06)] group-hover:border-espresso/10'
        )}
      >
        <div className="flex items-center gap-4">
          {/* Drag Handle Icon - The only area that triggers drag */}
          <div
            onPointerDown={e => controls.start(e)}
            className="text-espresso/20 group-hover:text-espresso/40 transition-colors p-2 -ml-2 cursor-grab active:cursor-grabbing touch-none"
          >
            <List size={20} weight="bold" />
          </div>

          <div
            className={cn(
              'w-[42px] h-[42px] rounded-full flex items-center justify-center shrink-0 transition-transform duration-500',
              widget.id.includes('silver')
                ? 'bg-[#9CA3AF]/10'
                : widget.id.includes('gold') || widget.id.includes('gram')
                  ? 'bg-[#DCB645]/10'
                  : 'bg-espresso/5'
            )}
          >
            {getIcon(widget.id)}
          </div>
          <div className="flex flex-col">
            <span className="font-bold text-[16px] text-espresso tracking-tight">
              {widget.label}
            </span>
          </div>
        </div>

        <button
          onClick={e => {
            e.stopPropagation()
            onToggleWidget(widget.id)
          }}
          className={cn('text-[28px] transition-all active:scale-95', 'text-[#DCB645]')}
        >
          <CheckCircle weight="fill" />
        </button>
      </div>
    </Reorder.Item>
  )
}
